import { memo, useCallback, useState } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import type { UserWithSites } from "@src/features/settings/data/mock-all-users";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";
import { AssignRolesUserList, type AssignRole } from "./AssignRolesUserList";

type Role = AssignRole;

interface AssignRolesBottomSheetOuterProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  selectedUsers: UserWithSites[];
  targetName: string;
  onSave: (assignments: { userId: string; role: Role }[]) => void | Promise<void>;
}

interface AssignRolesBottomSheetInnerProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  users: UserWithSites[];
  targetName: string;
  onSave: (assignments: { userId: string; role: Role }[]) => void | Promise<void>;
}

const AssignRolesBottomSheetInner = memo(
  ({ bottomSheetRef, users, targetName, onSave }: AssignRolesBottomSheetInnerProps) => {
    const { theme } = useUnistyles();
    const { bottom } = useSafeAreaInsets();
    const [roleMap, setRoleMap] = useState<Map<string, Role>>(() => new Map(users.map((user) => [user.id, "viewer"])));

    const handleRoleChange = useCallback((userId: string, role: Role) => {
      setRoleMap((prev) => {
        const next = new Map(prev);
        next.set(userId, role);
        return next;
      });
    }, []);

    const handleSave = useCallback(async () => {
      const assignments = users.map((user) => ({
        userId: user.id,
        role: roleMap.get(user.id) ?? ("viewer" as Role),
      }));
      try {
        await onSave(assignments);
      } catch {
        return;
      }
    }, [users, roleMap, onSave]);

    return (
      <AppBottomSheet
        bottomSheetRef={bottomSheetRef}
        snapPoints={["60%"]}
        enableDynamicSizing={false}
        backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
      >
        <View flex={1} style={[styles.container, { paddingBottom: bottom }]}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="4xl">
            <Text textStyle="lg/semibold">Assign roles for {targetName}</Text>
            <Text textStyle="sm/regular" color="textMuted">
              {users.length} selected
            </Text>
          </View>

          <BottomSheetScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
            <AssignRolesUserList users={users} roleMap={roleMap} onRoleChange={handleRoleChange} />
          </BottomSheetScrollView>

          <View pt="3xl">
            <Button variant="primary" label="Save" size="md" onPress={handleSave} />
          </View>
        </View>
      </AppBottomSheet>
    );
  },
);

interface AssignRolesBottomSheetProps extends AssignRolesBottomSheetOuterProps {}

export const AssignRolesBottomSheet = memo(
  ({ bottomSheetRef, selectedUsers, targetName, onSave }: AssignRolesBottomSheetProps) => {
    if (selectedUsers.length === 0) return null;

    const key = selectedUsers
      .map((user) => user.id)
      .sort()
      .join("-");

    return (
      <AssignRolesBottomSheetInner
        key={key}
        bottomSheetRef={bottomSheetRef}
        users={selectedUsers}
        targetName={targetName}
        onSave={onSave}
      />
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
    paddingTop: theme.spacing["5xl"],
    justifyContent: "center",
  },
  scrollContent: {
    flexGrow: 1,
  },
}));
